import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";
import {
  getRefreshResourcesStatusMock,
  getSoftwareOverviewBillingCostMock,
  getSoftwareOverviewMonthlyActiveUserRateMock,
  getSoftwareOverviewMonthlyCostMock,
  getSoftwareOverviewRecommendedActionsMock,
  getSoftwareOverviewRenewalDateMock,
  getSoftwareOverviewSavingCostMock,
  getSoftwareOverviewUserGrowthMock,
  getSoftwareOverviewUserMock,
  getSoftwareUsersMock,
} from "./mock";
import type {
  CustomAppState,
  GetAppIntegrationUsersParams,
  PatchExternalTenantSettingsReq,
  ReAuthenticateReq,
  ReAuthenticateRes,
  UpdateSoftwareArchitectureReq,
  UpdateSoftwareDetailReq,
} from "./type";
import type { CustomAppContractState } from "./type";
import type { PricingPlanState } from "./type";

export const getSoftware = async (softwareId: string) => {
  const res = await apiClient.GET("/v2/software/{softwareId}", {
    params: { path: { softwareId } },
  });
  return getDataOrThrowApiError(res);
};

export const getSoftwareOverviewUser = async (id: string) => {
  return await getSoftwareOverviewUserMock(id);
};

export const getSoftwareOverviewBillingCost = async (id: string) => {
  return await getSoftwareOverviewBillingCostMock(id);
};

export const getSoftwareOverviewUserGrowth = async (id: string) => {
  return await getSoftwareOverviewUserGrowthMock(id);
};

export const getSoftwareOverviewMonthlyActiveUserRate = async (id: string) => {
  return await getSoftwareOverviewMonthlyActiveUserRateMock(id);
};

export const getSoftwareOverviewMonthlyCost = async (id: string) => {
  return await getSoftwareOverviewMonthlyCostMock(id);
};

export const getSoftwareOverviewRenewalDate = async (id: string) => {
  return await getSoftwareOverviewRenewalDateMock(id);
};

export const getSoftwareOverviewSavingCost = async (id: string) => {
  return await getSoftwareOverviewSavingCostMock(id);
};

export const getSoftwareOverviewRecommendedActions = async (id: string) => {
  return await getSoftwareOverviewRecommendedActionsMock(id);
};

export const getCountByCategories = async (appIntegrationId: string) => {
  const res = await apiClient.GET("/v3/app-integrations/{id}/users/count", {
    params: { path: { id: appIntegrationId } },
  });
  return getDataOrThrowApiError(res);
};

// Refresh resources
export const postRefreshResources = async (id: string) => {
  const res = await apiClient.POST(
    "/v2/app-integrations/{id}/refresh-resources",
    {
      params: { path: { id } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const getRefreshResourcesStatus = async (id: string) => {
  return await getRefreshResourcesStatusMock(id);
};

export const getIntegratableApp = async (appId: string) => {
  const res = await apiClient.GET("/v2/apps/{id}", {
    params: { path: { id: appId } },
  });
  return getDataOrThrowApiError(res);
};

export const getAppIntegrationSettings = async (appIntegrationId: string) => {
  const res = await apiClient.GET("/v2/app-integrations/{id}/settings", {
    params: { path: { id: appIntegrationId } },
  });
  return getDataOrThrowApiError(res);
};

export const deactivateAppIntegration = async (appIntegrationId: string) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/deactivate",
    {
      params: { path: { id: appIntegrationId } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const deleteAppIntegration = async (appIntegrationId: string) => {
  const res = await apiClient.DELETE("/v2/app-integrations/{id}", {
    params: { path: { id: appIntegrationId } },
  });
  return getDataOrThrowApiError(res);
};

export const reactivateAppIntegration = async (appIntegrationId: string) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/reactivate",
    {
      params: { path: { id: appIntegrationId } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const patchExternalTenantSettings = async (
  appIntegrationId: string,
  tenantData: PatchExternalTenantSettingsReq,
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/external-tenant",
    {
      params: {
        path: {
          id: appIntegrationId,
        },
      },
      body: tenantData,
    },
  );
  return getDataOrThrowApiError(res);
};

export const updateCustomApp = async (
  appIntegrationId: string,
  appState: CustomAppState,
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/custom-app",
    {
      params: { path: { id: appIntegrationId } },
      body: { ...appState },
    },
  );
  return getDataOrThrowApiError(res);
};

export const getPresignedUrl = async (filename: string) => {
  const res = await apiClient.GET("/v2/s3/presigned-url/image", {
    params: { query: { filename } },
  });
  return getDataOrThrowApiError(res);
};

export const updateCustomAppContract = async (
  appIntegrationId: string,
  body: CustomAppContractState,
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/custom-app-contract",
    {
      params: { path: { id: appIntegrationId } },
      body,
    },
  );
  return getDataOrThrowApiError(res);
};

export const updatePricingPlan = async (
  appIntegrationId: string,
  pricingData: PricingPlanState,
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/pricing-plan",
    {
      params: { path: { id: appIntegrationId } },
      body: pricingData,
    },
  );
  return getDataOrThrowApiError(res);
};

export const updateAppUserProvisioning = async (
  appIntegrationId: string,
  body: { USER_PROVISIONING: boolean },
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/features",
    {
      params: { path: { id: appIntegrationId } },
      body,
    },
  );
  return getDataOrThrowApiError(res);
};

export const getSoftwareUsers = async (
  id: string,
  search: GetAppIntegrationUsersParams,
) => {
  return await getSoftwareUsersMock(id, search);
};

export const reAuthenticate = async (
  appIntegrationId: string,
  data: ReAuthenticateReq,
): Promise<ReAuthenticateRes> => {
  const res = await apiClient.POST(
    "/v2/app-integrations/{id}/settings/authenticate",
    {
      params: { path: { id: appIntegrationId } },
      body: data,
    },
  );
  return getDataOrThrowApiError(res);
};

export const deleteCustomAppUser = async (
  appIntegrationId: string,
  appUserId: string,
) => {
  const res = await apiClient.DELETE(
    "/v3/app-integrations/{id}/users/{appUserId}",
    {
      params: { path: { id: appIntegrationId, appUserId } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const updateSoftwareDetail = async (
  softwareId: string,
  data: UpdateSoftwareDetailReq,
) => {
  const res = await apiClient.PUT("/v2/software/{softwareId}/detail", {
    params: { path: { softwareId } },
    body: data,
  });
  return getDataOrThrowApiError(res);
};

export const updateSoftwareArchitecture = async (
  softwareId: string,
  data: UpdateSoftwareArchitectureReq,
) => {
  const res = await apiClient.PUT("/v2/software/{softwareId}/architecture", {
    params: { path: { softwareId } },
    body: data,
  });
  return getDataOrThrowApiError(res);
};

// ロゴ関連のAPI関数
export const getSoftwareLogoPresignedUrl = async (softwareId: string) => {
  const res = await apiClient.GET(
    "/v2/software/{softwareId}/logo/presigned-url",
    {
      params: { path: { softwareId } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const uploadSoftwareLogo = async (presignedUrl: string, file: File) => {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`Logo upload failed: ${response.statusText}`);
  }

  return response;
};

export const deleteSoftwareLogo = async (presignedUrl: string) => {
  const response = await fetch(presignedUrl, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(`Logo delete failed: ${response.statusText}`);
  }

  return response;
};
